import { LineChart } from '@tremor/react'
import type { QuestionTrendDataPoint } from './types'

interface Props {
  data: QuestionTrendDataPoint[]
}

export function QuestionTrendsChart({ data }: Props) {
  if (data.length === 0) {
    return (
      <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-5">
        <h3 className="font-medium text-stone-900 dark:text-stone-100">Question Trends Over Time</h3>
        <div className="h-72 flex items-center justify-center text-sm text-stone-500 dark:text-stone-400">
          No trend data available for the selected period
        </div>
      </div>
    )
  }

  // Extract question keys from data
  const questionKeys = Object.keys(data[0] || {})
    .filter(key => key.startsWith('q_') && !key.includes('_label') && !key.includes('_text'))

  if (questionKeys.length === 0) {
    return (
      <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-5">
        <h3 className="font-medium text-stone-900 dark:text-stone-100">Question Trends Over Time</h3>
        <div className="h-72 flex items-center justify-center text-sm text-stone-500 dark:text-stone-400">
          No question trends recorded in this period
        </div>
      </div>
    )
  }

  // Get question labels for display
  const questionLabels: Record<string, string> = {}
  questionKeys.forEach(key => {
    const textKey = `${key}_text`
    const label = data.find(d => d[textKey])?.[textKey] as string
    questionLabels[key] = label || key.replace('q_', 'Question ')
  })

  return (
    <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-5">
      <h3 className="font-medium text-stone-900 dark:text-stone-100">Question Trends Over Time</h3>
      <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
        Trend direction by question across reports
      </p>

      <div className="mt-4 [&_.tremor-Legend-root]:hidden [&_text]:text-[10px] [&_text]:fill-stone-400">
        <LineChart
          className="h-64"
          data={data}
          index="date"
          categories={questionKeys}
          colors={['teal', 'blue', 'purple', 'amber', 'emerald', 'rose', 'cyan']}
          yAxisWidth={32}
          showAnimation
          curveType="monotone"
          minValue={-1}
          maxValue={1}
          showLegend={false}
          valueFormatter={(value) => {
            if (value === 1) return 'Up'
            if (value === 0) return 'Stable'
            if (value === -1) return 'Down'
            return String(value)
          }}
        />
      </div>

      {questionKeys.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {questionKeys.map((key, index) => {
            const colors = ['bg-teal-500', 'bg-blue-500', 'bg-purple-500', 'bg-amber-500', 'bg-emerald-500', 'bg-rose-500', 'bg-cyan-500']
            const colorClass = colors[index % colors.length]
            return (
              <span
                key={key}
                className="inline-flex items-center gap-1.5 text-[10px] text-stone-500 dark:text-stone-400"
              >
                <span className={`w-2 h-2 rounded-full ${colorClass}`} />
                {questionLabels[key]}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}
